import { FC, useEffect, useState } from 'react';
import { ISurveysEntry } from '../../shared/interfaces/ISurveysEntry';
import { Box, LinearProgress, useTheme } from '@mui/material';
import SurveyService from '../../services/SurveyService';
import { useTranslation } from 'react-i18next';

interface Props {
  survey: ISurveysEntry;
}

interface NpsSurveyScores {
  netPromoterScore: number;
  detractors: number;
  promoters: number;
  passives: number;
  total: number;
}

const SurveyNpsBreakdown: FC<Props> = ({ survey }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<NpsSurveyScores | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const getScore = async () => {
      try {
        const response = await SurveyService.getNpsSurveyScore(
          survey.id,
          setLoading,
          controller.signal
        );
        if (response) {
          setData(response);
          setLoading(false);
        } else {
          return;
        }
      } catch (error) {
        console.error('Error fetching score:', error);
      }
    };

    getScore();

    return () => {
      controller.abort();
    };
  }, []);

  function renderTable() {
    if (data == null) {
      return null;
    }
    let promPerc = 0;
    if (data.total != 0) {
      promPerc = Math.round((data.promoters / data.total) * 100);
    }
    let detrPerc = 0;
    if (data.total != 0) {
      detrPerc = Math.round((data.detractors / data.total) * 100);
    }
    let passPerc = 0;
    if (data.total != 0) {
      passPerc = Math.round((data.passives / data.total) * 100);
    }
    return (
      <>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <td style={{ textAlign: 'center', fontSize: '1.2em', color: '#4eaf50' }}>
                {promPerc}%
              </td>
              <td style={{ textAlign: 'center', fontSize: '1.2em' }}>-</td>
              <td style={{ textAlign: 'center', fontSize: '1.2em', color: '#ea676d' }}>
                {detrPerc}%
              </td>
              <td style={{ textAlign: 'center', fontSize: '1.2em' }}>=</td>
              <td style={{ textAlign: 'center', fontSize: '1.2em', color: '#717077' }}>
                {Math.round(data.netPromoterScore)}
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'center', fontSize: '0.8em', paddingBottom: '5px' }}>
                {t('nps_promoters')}
              </td>
              <td style={{ textAlign: 'center', fontSize: '0.8em', paddingBottom: '5px' }}></td>
              <td style={{ textAlign: 'center', fontSize: '0.8em', paddingBottom: '5px' }}>
                {t('nps_detractors')}
              </td>
              <td style={{ textAlign: 'center', fontSize: '0.8em', paddingBottom: '5px' }}></td>
              <td style={{ textAlign: 'center', fontSize: '0.8em', paddingBottom: '5px' }}>
                {t('nps_score')}
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                colSpan={4}
                style={{ fontSize: '0.8em', borderTop: '1px solid #eeeeee', paddingTop: '5px' }}
              >
                <span style={{ color: '#717077', paddingRight: '3px' }}>
                  {t('nps_promoters')}&nbsp;(9/10)
                </span>
                <strong>{data.promoters}</strong>
              </td>
              <td style={{ borderTop: '1px solid #eeeeee', paddingTop: '5px' }}>
                <div style={{ width: '100px', height: '10px', backgroundColor: '#e7e7e7' }}>
                  <div
                    style={{ width: promPerc + 'px', height: '10px', backgroundColor: '#4eaf50' }}
                  ></div>
                </div>
              </td>
            </tr>
            <tr>
              <td
                colSpan={4}
                style={{ fontSize: '0.8em' }}
              >
                <span style={{ color: '#717077', paddingRight: '3px' }}>
                  {t('nps_passives')}&nbsp;(7/8)
                </span>
                <strong>{data.passives}</strong>
              </td>
              <td>
                <div style={{ width: '100px', height: '10px', backgroundColor: '#e7e7e7' }}>
                  <div
                    style={{ width: passPerc + 'px', height: '10px', backgroundColor: '#f6b31a' }}
                  ></div>
                </div>
              </td>
            </tr>
            <tr>
              <td
                colSpan={4}
                style={{ fontSize: '0.8em' }}
              >
                <span style={{ color: '#717077', paddingRight: '3px' }}>
                  {t('nps_detractors')}&nbsp;(0-6)
                </span>
                <strong>{data.detractors}</strong>
              </td>
              <td>
                <div style={{ width: '100px', height: '10px', backgroundColor: '#e7e7e7' }}>
                  <div
                    style={{ width: detrPerc + 'px', height: '10px', backgroundColor: '#ea676d' }}
                  ></div>
                </div>
              </td>
            </tr>
            <tr>
              <td
                colSpan={5}
                style={{ fontSize: '0.8em' }}
              >
                <span style={{ color: '#717077', paddingRight: '3px' }}>{t('nps_total')}</span>
                <strong>{data.total}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  }

  return (
    <Box
      sx={{
        minWidth: '200px',
      }}
    >
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        renderTable()
      )}
    </Box>
  );
};

export default SurveyNpsBreakdown;
